import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage, CLOUD_FUNCTIONS_BASE_URL } from '../auth/firebaseConfig';
import { getAuth } from 'firebase/auth';

export const uploadFile = async (
  file: File,
  path: string,
  useEndpointUpload = false
): Promise<string> => {
  try {
    if (useEndpointUpload) {
      // Extract propertyId from path (format: listings/{propertyId}/...)
      const pathParts = path.split('/');
      const propertyId = pathParts[1];

      if (!propertyId) {
        throw new Error('Invalid path: propertyId not found');
      }

      // Create FormData with single file (Busboy pattern from Medium article)
      const formData = new FormData();
      formData.append('file', file);

      // Call Cloud Function endpoint
      console.debug('[uploadFile] Calling endpoint uploadImages', {
        propertyId,
        path,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });

      // ⚠️ CRITICAL: Do NOT set Content-Type header - browser will automatically set it
      // with the multipart/form-data boundary. Setting it manually breaks Busboy parsing.

      // Get Firebase ID token for authentication
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/uploadImages?propertyId=${propertyId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            // Note: Do NOT set Content-Type header - browser handles it with boundary for multipart/form-data
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Endpoint upload failed: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();

      console.debug('[uploadFile] Endpoint response parsed', {
        success: data?.success,
        imageCount: data?.images?.length,
        firstUrl: data?.images?.[0]?.url,
      });

      if (!data.success || !data.images || data.images.length === 0) {
        throw new Error('Endpoint returned no images');
      }

      // Return first image URL (single file upload)
      return data.images[0].url;
    } else {
      // Client-side upload (original behavior)
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    }
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const uploadMultipleImages = async (
  files: FileList,
  basePath: string,
  useEndpointUpload = false
): Promise<string[]> => {
  if (useEndpointUpload) {
    // Extract propertyId from basePath (format: listings/{propertyId})
    const pathParts = basePath.split('/');
    const propertyId = pathParts[1];

    if (!propertyId) {
      throw new Error('Invalid basePath: propertyId not found');
    }

    // Create FormData with all files (Busboy pattern from Medium article)
    // Busboy backend will receive multiple 'file' fields and parse them correctly
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('file', file); // All files use same key 'file'
    });

    try {
      // Call Cloud Function endpoint with all files
      console.debug('[uploadMultipleImages] Calling endpoint uploadImages', {
        propertyId,
        basePath,
        fileCount: files.length,
        fileNames: Array.from(files).map((f) => f.name),
      });

      // ⚠️ CRITICAL: Do NOT set Content-Type header - browser will automatically set it
      // with the multipart/form-data boundary. Setting it manually breaks Busboy parsing.

      // Get Firebase ID token for authentication
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/uploadImages?propertyId=${propertyId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            // Note: Do NOT set Content-Type header - browser handles it with boundary
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Endpoint upload failed: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();

      console.debug('[uploadMultipleImages] Endpoint response parsed', {
        success: data?.success,
        imageCount: data?.images?.length,
        firstUrl: data?.images?.[0]?.url,
        totalProcessTimeMs: data?.requestId,
      });

      if (!data.success || !data.images) {
        throw new Error('Endpoint returned invalid response');
      }

      // Extract URLs from response (Busboy returns array of { url, path, filename, fileSize })
      return data.images.map((img: { url: string }) => img.url);
    } catch (error) {
      console.error('Error uploading multiple images via endpoint:', error);
      throw error;
    }
  } else {
    // Client-side upload (original behavior)
    const uploadPromises = Array.from(files).map((file, index) => {
      const path = `${basePath}/${Date.now()}_${index}_${file.name}`;
      return uploadFile(file, path);
    });

    try {
      const uploadedUrls = await Promise.all(uploadPromises);
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading multiple images:', error);
      throw error;
    }
  }
};

export const deleteImage = async (url: string): Promise<void> => {
  try {
    const fileRef = ref(storage, url);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

export const deleteImageByUrl = async (url: string): Promise<void> => {
  try {
    // Extract the path from the Firebase Storage URL
    // URL format: https://firebasestorage.googleapis.com/v0/b/bucket-name/o/path%2Fto%2Ffile.ext?alt=media&token=...
    const pathMatch = url.match(/\/o\/(.+?)(\?|$)/);
    if (!pathMatch) {
      throw new Error('Invalid Firebase Storage URL');
    }

    // Decode the URL-encoded path
    const encodedPath = pathMatch[1];
    const decodedPath = decodeURIComponent(encodedPath);

    const fileRef = ref(storage, decodedPath);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting image by URL:', error);
    throw error;
  }
};

export const listFilesInFolder = async (folderPath: string): Promise<string[]> => {
  try {
    const folderRef = ref(storage, folderPath);
    const result = await listAll(folderRef);
    const fileUrls = await Promise.all(result.items.map((itemRef) => getDownloadURL(itemRef)));
    return fileUrls;
  } catch (error) {
    console.error('Error listing files in folder:', error);
    throw error;
  }
};
